/**
 * The touch menu.
 *
 * This game is mainly played on an iPad and a phone, and every screen except the D-pad
 * was reachable only by a keyboard key — shop on P, cards on C, competitions on K, dig on
 * G, chat on T, settings on O. On a touchscreen there is no keyboard, so most of the game
 * was simply unreachable. This gives every action a button.
 */
export interface MenuAction {
  id: string
  label: string
  icon: string
  run(): void
  /** Hidden when this returns false — e.g. online-only actions while offline. */
  visible?(): boolean
}

export class TouchMenu {
  readonly root: HTMLDivElement
  private readonly sheet: HTMLDivElement
  private readonly button: HTMLButtonElement
  private readonly actions: MenuAction[]

  constructor(actions: MenuAction[]) {
    this.actions = actions

    this.root = document.createElement('div')
    this.root.className = 'menu'

    this.button = document.createElement('button')
    this.button.className = 'menu-button'
    this.button.innerHTML = '<span>MENU</span>'
    this.button.addEventListener('click', () => this.toggle())
    this.root.append(this.button)

    this.sheet = document.createElement('div')
    this.sheet.className = 'menu-sheet'
    this.sheet.hidden = true
    this.root.append(this.sheet)

    document.body.append(this.root)
  }

  get isOpen(): boolean {
    return !this.sheet.hidden
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible
  }

  close(): void {
    this.sheet.hidden = true
    this.button.classList.remove('is-open')
  }

  toggle(): void {
    if (this.isOpen) {
      this.close()
      return
    }
    // Pointer lock hides the cursor; a locked player cannot click these items at all.
    if (document.pointerLockElement) document.exitPointerLock()
    this.render()
    this.sheet.hidden = false
    this.button.classList.add('is-open')
  }

  private render(): void {
    this.sheet.innerHTML = ''
    for (const action of this.actions) {
      if (action.visible && !action.visible()) continue
      const item = document.createElement('button')
      item.className = 'menu-item'
      item.innerHTML = `<span class="menu-icon">${action.icon}</span><span>${action.label}</span>`
      item.addEventListener('click', () => {
        this.close()
        action.run()
      })
      this.sheet.append(item)
    }
  }
}
